/**
 * @fileoverview Centralized discover-fetcher error handling (WEB-268).
 *
 * The discover surface ships three RTK Query-backed views (list, map,
 * calendar) plus the mobile filter drawer. Pre-WEB-268, none of them surfaced
 * a user-visible failure when the backend rejected the active filter set with
 * a 4xx (the audit on 2026-05-14 caught date-chip clicks producing silent
 * 400s — the previous results stayed on screen and the user assumed their
 * filter had applied).
 *
 * This module solves the surface-wide problem in one place:
 *
 *   - `parseDiscoverError` — pure helper that reads the RTK Query error shape
 *     (`FetchBaseQueryError | SerializedError | unknown`) and returns a
 *     `{ kind, status, message }` summary. Each view passes its
 *     `queryResult.error` through this; no view writes its own decoder.
 *   - `useDiscoverErrorHandler` — hook that wires the parsed result to the
 *     global notification queue (`useNotification`) and the URL-state
 *     mutator (`useDiscoverParams.replaceParams`). Exactly ONE instance runs
 *     per Discover surface (WEB-1096): `DiscoverErrorHost` at template level
 *     mounts it, arms it on every params transition, and shares it with the
 *     list through `useDiscoverErrorHost()` (`discoverErrorHost.ts`). Never
 *     mount a second instance in an organism — every subscriber sees the
 *     same RTK error object and the notification store does not dedupe.
 *
 * Behaviour matrix:
 *
 *   | kind   | toast                                        | revert URL?    |
 *   |--------|----------------------------------------------|----------------|
 *   | 4xx    | red, copy = API `message`                    | yes            |
 *   | 5xx    | red, "Couldn't load events. Try again."      | no (transient) |
 *   | other  | no toast (network blip, abort, etc.)         | no             |
 *
 * Background failures (no user action since the last clear) are silenced via
 * the `isUserInitiated` flag tracked by `recordParams`. The flag is set when
 * the owner records a user action, and cleared when a toast fires or the
 * owner reports a clean settle (`clearUserInitiated`).
 */
import * as React from 'react';

import type { DiscoverParams } from './url-state';

/**
 * Categorical bucket the discover surface reacts to.
 *
 * - `rate-limited` — HTTP 429. Split out from the generic `4xx` bucket
 *   (WEB-289) so the empty state can show a "Try again in M:SS" countdown
 *   and disable Retry until the window clears, instead of offering a Retry
 *   that just re-429s.
 * - `4xx` — any other client error (400/404/422…). User-facing copy comes
 *   from the backend `message`.
 * - `5xx` — server-side / transient. Generic copy, Retry available.
 * - `network` — FETCH_ERROR / TIMEOUT_ERROR (dropped connection, DNS, timeout).
 *   Silent toast; empty state maps to offline / request-failed (WEB-938).
 * - `aborted` — intentional abort (superseded refetch / unmount). Never stamps
 *   `lastError`; never flashes an empty error state.
 * - `unknown` — PARSING_ERROR / CUSTOM_ERROR / serialized / odd HTTP. Silent
 *   toast; empty state is request-failed, never no-match (WEB-938).
 */
export type DiscoverErrorKind = 'rate-limited' | '4xx' | '5xx' | 'network' | 'aborted' | 'unknown';

/** HTTP status the backend rate limiter returns. */
const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * Fallback countdown (seconds) used when a 429 arrives with no usable
 * `Retry-After` header. We still show the rate-limited copy, but the
 * countdown starts from this conservative default rather than showing a
 * misleading "0:00 / retry now" that would just re-429. Mirrors the
 * backend limiter window order of magnitude without claiming precision.
 */
export const DEFAULT_RETRY_AFTER_SECONDS = 60;

/** Upper bound so a hostile / bogus header can't freeze Retry for hours. */
const MAX_RETRY_AFTER_SECONDS = 3600;

/**
 * Parse an RFC 9110 §10.2.3 `Retry-After` value into whole seconds.
 *
 * Accepts the two on-the-wire forms plus the already-numeric case:
 *   - **delta-seconds**: a non-negative integer string (`"217"`) or number.
 *   - **HTTP-date**: an absolute timestamp; we return the clamped delta from
 *     `now` (`Date.parse`-able, e.g. `"Wed, 21 Oct 2026 07:28:00 GMT"`).
 *
 * Defensive by contract — every un-parseable / missing / negative / absurd
 * input collapses to {@link DEFAULT_RETRY_AFTER_SECONDS} so the UI never
 * shows a nonsense countdown or crashes on novel header shapes.
 */
export function parseRetryAfter(
  raw: string | number | null | undefined,
  now: number = Date.now(),
): number {
  if (raw === null || raw === undefined) return DEFAULT_RETRY_AFTER_SECONDS;

  const clamp = (seconds: number): number => {
    if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_RETRY_AFTER_SECONDS;
    return Math.min(Math.ceil(seconds), MAX_RETRY_AFTER_SECONDS);
  };

  if (typeof raw === 'number') return clamp(raw);

  const trimmed = raw.trim();
  if (trimmed.length === 0) return DEFAULT_RETRY_AFTER_SECONDS;

  // delta-seconds form: a bare non-negative integer.
  if (/^\d+$/.test(trimmed)) {
    return clamp(Number.parseInt(trimmed, 10));
  }

  // HTTP-date form: absolute instant → delta from now.
  const target = Date.parse(trimmed);
  if (Number.isNaN(target)) return DEFAULT_RETRY_AFTER_SECONDS;
  return clamp((target - now) / 1000);
}

/**
 * Distilled view of an RTK Query error. The discover surface only needs the
 * bucket + the human-readable copy, so we collapse the FetchBaseQuery /
 * SerializedError union down to a stable shape.
 */
export interface DiscoverErrorSummary {
  /** Bucket the error falls into. Drives toast + revert decisions. */
  readonly kind: DiscoverErrorKind;
  /** Numeric HTTP status when one is available; `null` otherwise. */
  readonly status: number | null;
  /**
   * User-facing copy. For 4xx this is the backend `message` (e.g.
   * `'Invalid query parameters'`); for 5xx/unknown it is the canonical
   * generic line; for `rate-limited` it is the canonical rate-limit line
   * (the countdown digits are rendered by the empty state, not baked into
   * this string).
   */
  readonly message: string;
  /**
   * Seconds the caller should wait before retrying. Only populated when
   * `kind === 'rate-limited'` (parsed from the `Retry-After` header via
   * {@link parseRetryAfter}, defaulting to {@link DEFAULT_RETRY_AFTER_SECONDS}
   * when the header is absent / unparseable). `null` for every other kind.
   */
  readonly retryAfterSeconds: number | null;
}

/** Canonical generic message used when the backend returns 5xx/unknown. */
export const GENERIC_DISCOVER_ERROR_MESSAGE = "Couldn't load events. Try again.";

/**
 * Canonical copy for the rate-limited state. Deliberately omits the
 * countdown digits — the empty state owns the live `M:SS` so it can tick
 * without re-decoding the error.
 */
export const RATE_LIMITED_DISCOVER_ERROR_MESSAGE = 'Rate limited. Try again in';

/** Title shown in the toast — kept short so it fits the notification panel. */
export const DISCOVER_ERROR_TOAST_TITLE = 'Filter rejected';

/** Title shown in the toast when the failure is server-side / transient. */
export const DISCOVER_ERROR_TOAST_TITLE_TRANSIENT = 'Discover unavailable';

/** Title shown in the toast when the request was rate limited (429). */
export const DISCOVER_ERROR_TOAST_TITLE_RATE_LIMITED = 'Rate limited';

/**
 * Key the API layer folds the raw `Retry-After` header onto when a 429
 * comes back (see `packages/api/src/endpoints/discover.ts`). Kept in sync
 * with `RETRY_AFTER_DATA_KEY` there; duplicated rather than imported so this
 * pure module stays free of an `@loci/api` dependency edge.
 */
const RETRY_AFTER_DATA_KEY = '__retryAfterHeader';

/**
 * Pull the folded `Retry-After` header value off a 429 error payload. The
 * API layer stores the raw header string (or `null` when absent) under
 * {@link RETRY_AFTER_DATA_KEY}. Tolerates a bare numeric too in case a
 * future caller folds seconds directly.
 */
function extractRetryAfterRaw(payload: unknown): string | number | null {
  if (!isObject(payload)) return null;
  const value = payload[RETRY_AFTER_DATA_KEY];
  if (typeof value === 'string' || typeof value === 'number') return value;
  return null;
}

/**
 * True when `value` looks like an object (i.e. safe to index). Narrows away
 * `null` (which is `typeof === 'object'`) without dragging type predicates
 * into every call site.
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Pluck a string `message` field from an arbitrary error payload. Tolerates
 * the two shapes the backend emits (top-level `{ message }` envelope on the
 * `errorResponse` helper and Zod's `{ message }` on validation paths) plus a
 * generic fallback so we never throw on novel error shapes.
 */
const ABORT_NAME = 'AbortError';
const NETWORK_STATUSES = new Set(['FETCH_ERROR', 'TIMEOUT_ERROR']);

function isAbortError(error: Record<string, unknown>): boolean {
  if (error.name === ABORT_NAME) return true;
  return typeof error.error === 'string' && /abort/i.test(error.error);
}

function extractApiMessage(payload: unknown): string | null {
  if (!isObject(payload)) return null;
  const candidate = payload.message;
  if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  // Some endpoints (Zod-driven) nest copy under `error.message`. Be lenient.
  const nestedError = payload.error;
  if (isObject(nestedError)) {
    const nested = nestedError.message;
    if (typeof nested === 'string' && nested.length > 0) return nested;
  }
  return null;
}

/**
 * Decode the RTK Query error into the discover-surface summary shape. Pure —
 * no React, no dispatch — so we can unit-test every status bucket and the
 * "unknown shape" fallback without an act() wrapper.
 *
 * RTK Query emits two error shapes on a `useXxxQuery().error` field:
 *
 *  - `FetchBaseQueryError` — the shape produced by the JSON fetch base
 *    query. Carries `{ status: number, data: unknown }` on HTTP responses,
 *    or `{ status: 'FETCH_ERROR' | 'PARSING_ERROR' | …, error: string }`
 *    when the fetch fails before a response is received.
 *  - `SerializedError` — a redux-toolkit serialized Error from a thrown
 *    exception in the prepareHeaders / transformResponse path. Carries
 *    `{ message?: string, name?: string, … }`.
 *
 * `extractDiscoverError` accepts `unknown` so callers can pass
 * `queryResult.error` directly without manual narrowing.
 */
export function parseDiscoverError(error: unknown): DiscoverErrorSummary | null {
  if (error === null || error === undefined) return null;

  // FetchBaseQueryError with an HTTP response.
  if (isObject(error) && typeof error.status === 'number') {
    const status = error.status;
    // 429 first — it's a 4xx by range but gets its own bucket so the empty
    // state can render a countdown + disabled Retry instead of a Retry that
    // immediately re-429s (WEB-289). `retryAfterSeconds` always resolves to
    // a sane value (the parser defaults a missing/garbage header).
    if (status === HTTP_TOO_MANY_REQUESTS) {
      return {
        kind: 'rate-limited',
        status,
        message: RATE_LIMITED_DISCOVER_ERROR_MESSAGE,
        retryAfterSeconds: parseRetryAfter(extractRetryAfterRaw(error.data)),
      };
    }
    if (status >= 400 && status < 500) {
      const apiMessage = extractApiMessage(error.data);
      return {
        kind: '4xx',
        status,
        message: apiMessage ?? GENERIC_DISCOVER_ERROR_MESSAGE,
        retryAfterSeconds: null,
      };
    }
    if (status >= 500) {
      return {
        kind: '5xx',
        status,
        message: GENERIC_DISCOVER_ERROR_MESSAGE,
        retryAfterSeconds: null,
      };
    }
    // Status outside the 4xx/5xx range (e.g. a misbehaving proxy returning
    // 3xx after follow). Treat as transient.
    return {
      kind: 'unknown',
      status,
      message: GENERIC_DISCOVER_ERROR_MESSAGE,
      retryAfterSeconds: null,
    };
  }

  // FetchBaseQueryError with a string `status` (FETCH_ERROR, PARSING_ERROR,
  // TIMEOUT_ERROR, CUSTOM_ERROR). Abort first so a superseded refetch never
  // stamps lastError. FETCH_ERROR / TIMEOUT_ERROR are `network`; the rest
  // stay `unknown` (WEB-938).
  if (isObject(error) && typeof error.status === 'string') {
    if (isAbortError(error)) {
      return {
        kind: 'aborted',
        status: null,
        message: GENERIC_DISCOVER_ERROR_MESSAGE,
        retryAfterSeconds: null,
      };
    }
    const kind: DiscoverErrorKind = NETWORK_STATUSES.has(error.status) ? 'network' : 'unknown';
    return {
      kind,
      status: null,
      message: GENERIC_DISCOVER_ERROR_MESSAGE,
      retryAfterSeconds: null,
    };
  }

  // SerializedError (or any object that just has a `message`). AbortError
  // from RTK unwrap (`{ name: 'AbortError', message: 'Aborted' }`) is not a
  // user-visible failure. Everything else stays unknown — we don't expose
  // internal error text.
  if (isObject(error)) {
    if (isAbortError(error)) {
      return {
        kind: 'aborted',
        status: null,
        message: GENERIC_DISCOVER_ERROR_MESSAGE,
        retryAfterSeconds: null,
      };
    }
    return {
      kind: 'unknown',
      status: null,
      message: GENERIC_DISCOVER_ERROR_MESSAGE,
      retryAfterSeconds: null,
    };
  }

  return null;
}

/* ─── Hook ─────────────────────────────────────────────────────────────── */

/**
 * Minimal contract a notification host must satisfy. The production wiring
 * uses `useNotification` from `@loci/store`; tests inject a spy so we can
 * assert exactly which notification was queued.
 */
export interface DiscoverNotificationHost {
  showNotification: (input: {
    type: 'error';
    title: string;
    message: string;
    /**
     * Priority bumped to `high` so the toast doesn't get bumped out of the
     * `maxVisible` window by background success notifications (the user
     * needs to see why their filter didn't apply).
     */
    priority?: 'high';
  }) => void;
}

/**
 * Minimal contract a URL-state host must satisfy. Production wiring is
 * `useDiscoverParams().replaceParams`; tests inject a spy.
 */
export interface DiscoverParamsHost {
  replaceParams: (
    next: DiscoverParams,
    options?: { readonly history?: 'push' | 'replace' },
  ) => void;
}

export interface UseDiscoverErrorHandlerOptions {
  /**
   * Notification host used to display toasts. Required — the hook never
   * silently no-ops when the error is user-initiated.
   */
  readonly notifications: DiscoverNotificationHost;
  /**
   * URL-state host used to revert filter state on 4xx. Required.
   */
  readonly paramsHost: DiscoverParamsHost;
}

export interface UseDiscoverErrorHandlerResult {
  /**
   * Record the revert target for the next 4xx. `DiscoverErrorHost` calls it
   * after each user params transition with the params from BEFORE it; `null`
   * means the action kept the URL (load more, retry), so there is nothing to
   * revert to and a 4xx only toasts.
   *
   * `isUserInitiated` unmutes the next toast — failures with no recorded user
   * action since the last clear are background noise and stay silent.
   */
  readonly recordParams: (
    params: DiscoverParams | null,
    options?: { isUserInitiated?: boolean },
  ) => void;
  /**
   * Process an RTK Query `queryResult.error`. Idempotent across re-renders
   * of the same error reference — internal guard prevents re-firing the
   * toast or re-reverting on every render of a component that observes the
   * error.
   *
   * Returns the parsed summary so callers can also tweak local UI (e.g.
   * the empty state copy).
   *
   * `stale: true` marks an error that predates the user's action (WEB-1096):
   * it still updates `lastError`, but takes the background path — no toast,
   * no revert, and the user-initiated flag stays armed for the real answer.
   */
  readonly handleError: (
    error: unknown,
    options?: { readonly stale?: boolean },
  ) => DiscoverErrorSummary | null;
  /**
   * The most recent parsed summary, refreshed on each successful
   * `handleError` call. Exposed so the View layer can swap the empty state
   * copy without re-decoding the error.
   */
  readonly lastError: DiscoverErrorSummary | null;
  /**
   * Clear the user-initiated flag. The owner calls it when the user's request
   * settles cleanly, so a later background failure stays silent.
   */
  readonly clearUserInitiated: () => void;
}

/**
 * Wire the parsed error to the global notification queue + URL-state
 * mutator. The hook is intentionally a thin coordination layer; the
 * decision logic lives in `parseDiscoverError` (pure) so it can be unit
 * tested without React.
 */
export function useDiscoverErrorHandler({
  notifications,
  paramsHost,
}: UseDiscoverErrorHandlerOptions): UseDiscoverErrorHandlerResult {
  // WEB-1096: read the hosts through refs so every callback — and the result
  // object — stays referentially stable whatever the caller passes
  // (`useNotification()` returns a new object each render). The owner's
  // effects depend on these callbacks: `useReportDiscoverQuery` must re-run
  // only when its query changes, and an effect keyed on an unstable handler
  // re-runs every render (pre-WEB-1096 Map view re-armed the flag on each).
  const notificationsRef = React.useRef(notifications);
  notificationsRef.current = notifications;
  const paramsHostRef = React.useRef(paramsHost);
  paramsHostRef.current = paramsHost;
  const previousParamsRef = React.useRef<DiscoverParams | null>(null);
  const isUserInitiatedRef = React.useRef<boolean>(false);
  // Guards against re-firing the same error across React re-renders. RTK
  // Query keeps the same error reference until the next request resolves,
  // so we key the guard by reference identity.
  const lastHandledErrorRef = React.useRef<unknown>(null);
  const [lastError, setLastError] = React.useState<DiscoverErrorSummary | null>(null);

  const recordParams = React.useCallback<UseDiscoverErrorHandlerResult['recordParams']>(
    (params, options) => {
      previousParamsRef.current = params;
      if (options?.isUserInitiated) {
        isUserInitiatedRef.current = true;
      }
    },
    [],
  );

  const clearUserInitiated = React.useCallback(() => {
    isUserInitiatedRef.current = false;
  }, []);

  // Tracks the error reference whose summary is reflected in `lastError`
  // state. Used to avoid re-calling `setLastError` for the same error on
  // every render (which would trigger an infinite render loop because the
  // summary object identity changes on each parse).
  const lastSummarizedErrorRef = React.useRef<unknown>(null);

  const handleError = React.useCallback<UseDiscoverErrorHandlerResult['handleError']>(
    (error, options) => {
      // No error → reset guards so the *next* error fires its toast cleanly.
      if (error === null || error === undefined) {
        lastHandledErrorRef.current = null;
        if (lastSummarizedErrorRef.current !== null) {
          lastSummarizedErrorRef.current = null;
          setLastError(null);
        }
        return null;
      }

      const summary = parseDiscoverError(error);
      if (summary === null) return null;

      // Abort is a superseded refetch / unmount, not a failure. Do not stamp
      // lastError or the empty state would flash on filter-change aborts.
      if (summary.kind === 'aborted') {
        return summary;
      }

      // Only push to React state when the error reference changes — saves
      // the surface from infinite-loop re-renders when the effect that
      // calls `handleError` depends on `error` and `error` is reference-
      // stable across renders.
      if (lastSummarizedErrorRef.current !== error) {
        lastSummarizedErrorRef.current = error;
        setLastError(summary);
      }

      // Background failures (no user-initiated action recorded since last
      // clear) are silent — they're noisy and confusing to surface. We
      // deliberately do NOT mark the error as "handled" in this path: if
      // the user *then* clicks a filter and the same error remains (e.g.
      // because the URL the user just toggled still maps to the same
      // failing request), `handleError` should re-evaluate on the next
      // call and emit a toast. A `stale` error (it predates the user's
      // request) takes the same path and leaves the flag armed.
      if (!isUserInitiatedRef.current || options?.stale) {
        return summary;
      }

      // User-initiated path. Suppress duplicate toasts for the same error
      // reference: subsequent React re-renders that pass the same `error`
      // object must not re-fire the toast or re-revert the URL.
      if (lastHandledErrorRef.current === error) {
        return summary;
      }
      lastHandledErrorRef.current = error;
      // Single toast per user-initiated action. Clear the flag so the
      // matching background revalidation that follows doesn't double-toast.
      isUserInitiatedRef.current = false;

      if (summary.kind === '4xx') {
        notificationsRef.current.showNotification({
          type: 'error',
          title: DISCOVER_ERROR_TOAST_TITLE,
          message: summary.message,
          priority: 'high',
        });
        if (previousParamsRef.current) {
          paramsHostRef.current.replaceParams(previousParamsRef.current, { history: 'replace' });
        }
      } else if (summary.kind === 'rate-limited') {
        notificationsRef.current.showNotification({
          type: 'error',
          title: DISCOVER_ERROR_TOAST_TITLE_RATE_LIMITED,
          message: GENERIC_DISCOVER_ERROR_MESSAGE,
          priority: 'high',
        });
        // Deliberately do NOT revert on 429 — the filter set is valid; the
        // request was throttled. Reverting would discard the user's work and
        // wouldn't clear the limit anyway. The empty state owns the live
        // countdown + disabled Retry.
      } else if (summary.kind === '5xx') {
        notificationsRef.current.showNotification({
          type: 'error',
          title: DISCOVER_ERROR_TOAST_TITLE_TRANSIENT,
          message: summary.message,
          priority: 'high',
        });
        // Deliberately do NOT revert on 5xx — the URL is still valid; the
        // server simply hiccuped. Reverting would discard the user's work.
      }
      // `network` / `unknown` / `aborted` stay silent (WEB-938). The empty
      // state owns the user-visible copy for transport failures.
      return summary;
    },
    [],
  );

  return React.useMemo(
    () => ({ recordParams, handleError, lastError, clearUserInitiated }),
    [recordParams, handleError, lastError, clearUserInitiated],
  );
}
